import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, TrendingUp } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('nutrinori_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchLinkStats();
  }, [slug]);

  const fetchLinkStats = async () => {
    try {
      const token = localStorage.getItem('nutrinori_token');
      const response = await axios.get(`${API}/links/${slug}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('nutrinori_token');
        navigate('/admin/login');
      } else if (error.response?.status === 404) {
        navigate('/admin/links');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#3D6B4A' }}></div>
        </div>
      </AdminLayout>
    );
  }

  if (!stats) return null;

  return (
    <AdminLayout>
      <div>
        <button
          onClick={() => navigate('/admin/links')}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#3D6B4A' }}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux liens
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="link-detail-title">
            {stats.name}
          </h1>
          <p className="mt-2 font-mono" style={{ color: '#6B756D' }}>/{stats.slug}</p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="card" data-testid="link-total-clicks">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#6B756D' }}>Total Clics</span>
              <TrendingUp className="w-5 h-5" style={{ color: '#3D6B4A' }} />
            </div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }}>
              {stats.total_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card">
            <div className="text-sm font-medium mb-2" style={{ color: '#6B756D' }}>iOS</div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }}>
              {stats.ios_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card">
            <div className="text-sm font-medium mb-2" style={{ color: '#6B756D' }}>Android</div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }}>
              {stats.android_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card">
            <div className="text-sm font-medium mb-2" style={{ color: '#6B756D' }}>Desktop</div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }}>
              {stats.desktop_clicks.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }}>
            Évolution (7 derniers jours)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(61, 107, 74, 0.1)" />
              <XAxis dataKey="date" stroke="#6B756D" />
              <YAxis stroke="#6B756D" />
              <Tooltip />
              <Line type="monotone" dataKey="clicks" stroke="#3D6B4A" strokeWidth={3} dot={{ fill: '#3D6B4A', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Countries */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }}>
            Top Pays
          </h2>
          <div className="space-y-3">
            {stats.countries.length > 0 ? (
              stats.countries.map((country, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(61, 107, 74, 0.1)' }}>
                  <span style={{ color: '#3A453F' }}>{country.country}</span>
                  <span className="font-semibold" style={{ color: '#3D6B4A' }}>{country.count}</span>
                </div>
              ))
            ) : (
              <p style={{ color: '#6B756D' }}>Aucune donnée disponible</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LinkDetail;
